import { getApiBaseUrl } from '../services/ApiUtils';
import React, { useState, useEffect,useContext, useRef, Suspense, lazy } from 'react';
import { Button, Typography, FormControl, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessAlarm';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import firebase from "firebase/app";
import "firebase/database";
import "./ExamScreen.css";
import DOMPurify from 'dompurify';
import ExamSecurity from './ExamSecurity';
import SubmissionProgressModal from './SubmissionProgressModal';
import AuthenticatedImage from './AuthenticatedImage';

const Detection = lazy(() => import('./EnhancedDetections'));

const TextBasedExamScreen = (props) => {
  if (document.addEventListener) {
    document.addEventListener('contextmenu', function (e) {
      e.preventDefault();
    }, false);
  }
  const [count_facedetect, setCountFacedetect] = useState(parseInt(sessionStorage.getItem("count_facedetect") || "0", 10));
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(parseInt(sessionStorage.getItem("exam_timer")) || 10);
  const [seconds, setSeconds] = useState(parseInt(sessionStorage.getItem("exam_sec")) || 0);
  const history = useHistory();
  const [isPshycoTest,setisPshycoTest] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submissionInProgress, setSubmissionInProgress] = useState(0);
  const [companyEntries, setCompanyEntries] = useState(['', '', '']);
  const [submissionProgress, setSubmissionProgress] = useState({
    open: false,
    currentStep: 0,
    progress: 0,
    status: 'processing',
    errorMessage: ''
  });
  const securityRef = useRef(null);
  const isLoadTesting = (typeof process !== 'undefined' && process && process.env && process.env.REACT_APP_LOAD_TESTING === '1');
  // const { logErrorToFirebase } = useContext(AuthContext);
  useEffect(() => {
    let isMounted = true; // flag to check if component is mounted

    // Initialize security system only if not in load testing mode
    if (!isLoadTesting) {
      securityRef.current = new ExamSecurity();
      securityRef.current.init({
        webcamRequired: true,
        fullscreenRequired: true,
        onCheatDetected: (type, count, message) => {
          if (isMounted && !submissionInProgress) {
            // Cheat detected - handled by security system
          }
        },
        onFullscreenExit: () => {
          if (isMounted && !submissionInProgress) {
            history.push("/enhanced-fullscreen-alert");
          }
        }
      });
    }

    // Fetch exam data
    const fetchExamData = async () => {
      // Check if user is authenticated before accessing Firebase
      const currentUser = firebase.auth().currentUser;
      if (!currentUser && !isLoadTesting) {
        console.error("User not authenticated");
        swal("Authentication Error", "Authentication is required to access the assessment.", "error");
        history.push("/login");
        return;
      }

      const examCode = sessionStorage.getItem("formvalid");
      if (examCode) {
        const examRef = firebase.database().ref(`TestPapers/${examCode}`);
        const snapshot = await examRef.once('value');
        const data = snapshot.val();
        if (isMounted && data) {
          if (data.isPshyco === 1) {
            setisPshycoTest(1);
          }
          if(data.isPshyco === 2){
            setisPshycoTest(2);
          }
          const loadedQuestions = data.questions || [];
          const shuffledQuestions = shuffleArray(loadedQuestions); // Shuffle only once when data is fetched
          setQuestions(shuffledQuestions); // Set the shuffled questions
          setLoading(false);
        } else if (isMounted) {
          swal("Error", "No assessment data found!", "error");
          history.push('/error');
        }
      } else if (isMounted) {
        swal("Error", "Invalid assessment code!", "error");
        history.push('/error');
      }
    };

    fetchExamData();

    // Cleanup all event listeners and interval on component unmount
    return () => {
      isMounted = false; // Set to false when the component unmounts
      if (securityRef.current) {
        securityRef.current.cleanup();
      }
    };
  }, []);

  const shuffleArray = (array) => {
    // Map each question to include its original index
    const indexedArray = array.map((question, index) => ({
        ...question,
        originalIndex: index, // Store the original index
    }));

    // Shuffle the indexed array
    return indexedArray.sort(() => Math.random() - 0.5);
  };

  const handleAnswerChange = (questionIndex, option,isPshycoTest) => {
    if(isPshycoTest === 1){
      const sanitizedValue = option
      .toUpperCase() // Convert to uppercase
      .replace(/[^ABCD]/g, '') // Allow only A, B, C, D
      .slice(0, 4); // Ensure length is at most 4
      setAnswers(prevAnswers => ({
        ...prevAnswers,
        [questionIndex]: sanitizedValue,
      }));
    }else if (isPshycoTest === 2) {
      // For company validation, we'll use separate state
      const newEntries = [...companyEntries];
      newEntries[questionIndex] = option;
      setCompanyEntries(newEntries);
    }
    else{
      setAnswers(prevAnswers => ({
        ...prevAnswers,
        [questionIndex]: option,
      }));
    }
  };


  const validateCompanyExistence = async (companyName) => {
    if (!companyName || typeof companyName !== 'string') {
      return { valid: false, message: "Invalid company name format", value:companyName };
    }
  
    const cleanedName = companyName.trim();
    if (!cleanedName) {
      return { valid: false, message: "Empty company name", value:companyName };
    }
  
    try {
  
      // 2. Clearbit Logo Check
      const domain = cleanedName.toLowerCase().replace(/\s+/g, '') + '.com';
      const clearbitRes = await fetch(
        `https://logo.clearbit.com/${domain}?size=80&format=png`
      );
      
      if (clearbitRes.status === 200) {
        return { 
          valid: true, 
          source: 'Clearbit', 
          message: `Verified company domain: ${domain}`,
          domain: domain, value:companyName
        };
      }
  
      // 3. Google Search Check (using "company" keyword)
      const googleRes = await fetch(
        `https://www.google.com/search?q=${encodeURIComponent(`"${cleanedName}" company`)}`
      );
      const html = await googleRes.text();
      const hasGoogleResults = !html.includes('did not match any documents') && 
                            html.includes(cleanedName);
  
      if (hasGoogleResults) {
        return {
          valid: true,
          source: 'Google',
          message: `Found relevant Google search results`, value:companyName
        };
      }
  
      return { 
        valid: false, 
        message: "No public records found in Wikipedia, Clearbit, or Google",
        details: {
          wikipedia: false,
          clearbit: false,
          google: false
        }, value:companyName
      };
  
    } catch (error) {
      return { 
        valid: false, 
        message: "Verification service unavailable",
        error: true, value:companyName
      };
    }
  };

  const validateEmail = async (email) => {
    // 1. Basic Regex Check (Always free)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: "Invalid email format", value:email };
    }
  
    // 2. Domain MX Record Check (Free via DNS lookup)
    try {
      const domain = email.split('@')[1];
      const mxRecords = await checkMxRecords(domain);
      if (mxRecords.length === 0) {
        return { valid: false, message: "Domain has no mail server", value:email };
      }
    } catch (error) {
    }
  
    // 3. Disposable Email Check (Free dataset)
    const isDisposable = await checkDisposableEmail(email);
    if (isDisposable) {
      return { valid: false, message: "Disposable email not allowed", value:email };
    }
  
    return { 
      valid: true, 
      message: "Email validated (basic checks passed)",
      details: {
        mxRecords: true,
        disposable: false,
        syntax: true
      }, value:email
    };
  };
  
  // Helper Functions:
  
  // Check MX records using public DNS
  const checkMxRecords = async (domain) => {
    try {
      // Use Cloudflare DNS-over-HTTPS (free)
      const response = await fetch(`https://cloudflare-dns.com/dns-query?name=${domain}&type=MX`, {
        headers: { 'Accept': 'application/dns-json' }
      });
      const data = await response.json();
      return data.Answer?.filter(r => r.type === 15) || [];
    } catch (error) {
      return [];
    }
  };
  
  // Check against free disposable email list
  const checkDisposableEmail = async (email) => {
    try {
      // Load from public GitHub repository
      const response = await fetch('https://raw.githubusercontent.com/disposable-email-domains/disposable-email-domains/master/disposable_email_blocklist.conf');
      const text = await response.text();
      const domain = email.split('@')[1];
      return text.includes(domain);
    } catch (error) {
      return false;
    }
  };

  const validateLinkedInUrl = (url) => {
    // Enhanced regex pattern covering all valid LinkedIn URL formats
    const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|pub|school|groups)\/[a-zA-Z0-9-]+(\/?|\/[a-zA-Z0-9-]*\/?)(\?[^\/]*)?$/i;
    
    // Basic structure validation
    if (!linkedinRegex.test(url)) {
      return {
        valid: false,
        message: "Invalid LinkedIn URL format. Must be in format: linkedin.com/in/username OR linkedin.com/company/name",
        value : url
      };
    }
  
    // Parse URL components
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);
    const entityType = pathParts[0]; // 'in', 'company', etc.
    const entityName = pathParts[1];
  
    // Additional validation rules
    const validationRules = {
      minNameLength: 3,
      maxNameLength: 100,
      allowedSpecialChars: '-'
    };
  
    // Validate entity name
    if (entityName.length < validationRules.minNameLength) {
      return {
        valid: false,
        message: `Profile/company name too short (minimum ${validationRules.minNameLength} characters)`,
        value : url
      };
    }
  
    if (entityName.length > validationRules.maxNameLength) {
      return {
        valid: false,
        message: `Profile/company name too long (maximum ${validationRules.maxNameLength} characters)`,
        value : url
      };
    }
  
    if (!/^[a-zA-Z0-9-]+$/.test(entityName)) {
      return {
        valid: false,
        message: `Invalid characters in profile/company name (only letters, numbers and hyphens allowed)`,
        value : url
      };
    }
  
    // Return successful validation
    return {
      valid: true,
      message: "Valid LinkedIn URL",
      type: entityType === 'in' ? 'personal' : 
            entityType === 'company' ? 'company' :
            entityType === 'school' ? 'school' :
            entityType === 'groups' ? 'group' : 'other',
      name: entityName,
      value : url
    };
  };

  const analyzeReasonQuality = async (reason, expectedReasonsString) => {
    // Convert pipe-separated string into array of expected reasons
    const expectedReasons = expectedReasonsString.split('|').map(r => r.trim()).filter(Boolean);
    
    // Simple keyword matching (no AI)
    const basicCheck = () => {
      const reasonLower = reason.toLowerCase();
      const matches = expectedReasons.filter(r => reasonLower.includes(r.toLowerCase()));
      const similarity = matches.length / expectedReasons.length;
      
      return {
        similarity,
        isValid: similarity >= 0.6,
        message: matches.length > 0 
          ? `Matched keywords: ${matches.join(', ')}` 
          : "No matching keywords found",
        value : reason
      };
    };
  
    // Skip AI for short/empty reasons
    if (!reason || reason.length < 10) return basicCheck();
  
    try {
      const baseUrl = getApiBaseUrl();      
      // Make API request to backend
      const response = await fetch(`${baseUrl}/api/validate-reason`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reason,
          expectedReasons
        })
      });

      if (!response.ok) {
        return basicCheck();
      }

      const result = await response.json();
      
      if (result.success) {
        const analysis = result.data;
        return {
          similarity: analysis.confidence,
          isValid: analysis.match,
          message: analysis.analysis || 
            (analysis.matched_keywords?.length 
              ? `Matches: ${analysis.matched_keywords.join(', ')}` 
              : "No matches found"),
          value : reason
        };
      } else {
        return basicCheck();
      }
      
    } catch (error) {
      console.error('Reason validation error:', error);
      return basicCheck();
    }
  };

  const validateCompanyEntry = async (entry, expectedReasons) => {
    const parts = entry.split('|').map(p => p.trim());
    if (parts.length < 4) {
      return { valid: false, message: "Invalid format", score: 0 };
    }

    const [name, email, linkedin, reason] = parts;
    
    const [company, emailVal, linkedinVal,checkReason] = await Promise.all([
      validateCompanyExistence(name),
      validateEmail(email),
      validateLinkedInUrl(linkedin),
      analyzeReasonQuality(reason, expectedReasons)
    ]);

    
    
    // // Call the function
    // const checkReason = async (reason, expectedReasons) => {
    //   try {
    //     const reasonAnalysis = await analyzeReasonQuality(reason, expectedReasons);
    //   } catch (error) {
    //   }
    // };

    
    // const reasonAnalysis = analyzeReasonQuality(reason, expectedReasons);
    
      // Calculate score (max 10 per company)
      let score = 0;
      if (company.valid) score += 3;
      if (emailVal.valid) score += 2;
      if (linkedinVal.valid) score += 2;
      if (checkReason.isValid) score += 3;

      return {
        valid: score >= 6,
        score,
        details: { company, email: emailVal, linkedin: linkedinVal, reason: checkReason },
        message: `Score: ${score}/10`
      };
  };




  const handleSubmit = (e) => {
    e.preventDefault();
      // For company validation, check at least one entry is provided
      const emptyIndexes = [0, 1, 2].filter(i => {
        return !companyEntries[i] || !companyEntries[i].trim();
      });
    
      if (emptyIndexes.length > 0) {
        swal(
          "Error",
          `Please fill out all fields. Missing entries at: ${emptyIndexes.map(i => i + 1).join(', ')}`,
          "error"
        );
        return;
      }
      
      swal({
        title: "Are you sure?",
        text: "Do you want to submit your company details?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((willSubmit) => {
        if (willSubmit) {
          // Pause security alerts during submission
          if (securityRef.current) {
            securityRef.current.pauseSecurityAlerts();
          }

          // Show submission progress modal
          setSubmissionProgress({
            open: true,
            currentStep: 0,
            progress: 0,
            status: 'processing',
            errorMessage: ''
          });

          setSubmissionInProgress(true);
          calculateAndSaveResultForCompanyValidation();
        }
      });
    
  };
  function createAlertContent(messageParts) {
    const div = document.createElement("div");
    div.style.textAlign = "left";
    div.style.fontSize = "16px";
    div.style.lineHeight = "1.6";
    div.innerHTML = messageParts.map(part => `<div style="margin-bottom: 20px;">${part}</div>`).join("");
    return div;
  }

const calculateAndSaveResultForCompanyValidation = async () => {
  swal({
    title: "Validating...",
    text: "Please wait while we validate your company details",
    icon: "info",
    buttons: false,
    closeOnClickOutside: false,
  });

  try {

    const examCode = sessionStorage.getItem("formvalid");
    const userEmail = sanitizeEmail(sessionStorage.getItem("checkemail"));
    const count_facedetect = sessionStorage.getItem("count_facedetect");
    const count_fullscreen = sessionStorage.getItem("count_fullscreen");
    const count_tabchange = sessionStorage.getItem("count_tabchange");
    const countalt = sessionStorage.getItem("countalt");
    const countctrl = sessionStorage.getItem("countctrl");
    const checkn = sessionStorage.getItem("checkname");
    const checke = sessionStorage.getItem("checkemail");
    const photo = sessionStorage.getItem("imageSrc");


    const examRef = firebase.database().ref(`TestPapers/${examCode}/questions`);
    const snapshot = await examRef.once('value');
    const examData = snapshot.val();
    const expectedReasons = examData[0].correctOption || [];
    const entries = companyEntries.filter(e => e.trim());
    
    const results = [];
    let totalScore = 0;

    for (let i = 0; i < entries.length; i++) {
      const result = await validateCompanyEntry(entries[i], expectedReasons);
      results.push(result);
      totalScore += result.score;
    }

    // Calculate final score (max 30 for 3 companies)
    const finalScore = Math.min(totalScore, 30);
    const uniqueUserRef = `Exam_results/${examCode}/${userEmail}`;

        // Retry logic
        const maxRetries = 3;
        let attempt = 0;

        // Get cheat counts from session storage
        const cheatCounts = {
            alt: sessionStorage.getItem("countalt") || "0",
            ctrl: sessionStorage.getItem("countctrl") || "0",
            tab: sessionStorage.getItem("count_tabchange") || "0",
            face: sessionStorage.getItem("count_facedetect") || "0",
            fullscreen: sessionStorage.getItem("count_fullscreen") || "0",
            copyPaste: sessionStorage.getItem("count_copypaste") || "0",
            webcamOff: sessionStorage.getItem("count_webcamoff") || "0",
            contextMenu: sessionStorage.getItem("count_contextmenu") || "0",
            noPerson: sessionStorage.getItem("count_noPerson") || "0",
            multiplePersons: sessionStorage.getItem("count_multiplePersons") || "0",
            suspiciousObjects: sessionStorage.getItem("count_suspiciousObjects") || "0"
        };

        // Calculate total cheat count (only main violations: copy/paste, tab switch, webcam)
        const totalCheatCount = parseInt(cheatCounts.copyPaste) + parseInt(cheatCounts.tab) + parseInt(cheatCounts.webcamOff);

        const saveResult = () => {
            // Update progress
            setSubmissionProgress(prev => ({
                ...prev,
                currentStep: 1,
                progress: 50
            }));

            // For load testing, save to separate collection
            if (isLoadTesting) {
                const userEmail = sessionStorage.getItem("checkemail") || 'load-test-user';
                const sanitizedEmail = userEmail.replace(/\./g, ',');
                const uniqueEmail = `${sanitizedEmail}_${Date.now()}`;
                const loadTestRef = firebase.database().ref(`Load_Testing/${examCode}/${uniqueEmail}`);
                const loadTestData = {
                    examCode,
                    answers: companyEntries,
                    score: finalScore,
                    totalQuestions: questions.length,
                    sectionScores: 0,
                    questionResults: results,
                    ...cheatCounts,
                    totalCheatCount,
                    semail: checke,
                    sname: checkn,
                    photo: photo,
                    isLoadTest: true,
                    loadTestTimestamp: Date.now()
                };

                loadTestRef.set(loadTestData)
                .then(() => {
                    setSubmissionProgress(prev => ({
                        ...prev,
                        currentStep: 2,
                        progress: 100,
                        status: 'success'
                    }));

                    setTimeout(() => {
                        setSubmissionProgress(prev => ({ ...prev, open: false }));
                        history.push('/thankyou');
                        setSubmissionInProgress(false);
                    }, 2000);
                })
                .catch(error => {
                    setSubmissionProgress(prev => ({
                        ...prev,
                        status: 'error',
                        errorMessage: 'Load testing submission failed.'
                    }));

                    setTimeout(() => {
                        setSubmissionProgress(prev => ({ ...prev, open: false }));
                        setSubmissionInProgress(false);
                    }, 3000);
                });
                return;
            }

            firebase.database().ref(uniqueUserRef).set({
                answers: companyEntries,
                score: finalScore,
                totalQuestions: questions.length,
                sectionScores: 0,
                questionResults: results,
                ...cheatCounts,
                totalCheatCount,
                semail: checke,
                sname: checkn,
                photo: photo
            })
            .then(() => {
                // Update progress to completion
                setSubmissionProgress(prev => ({
                    ...prev,
                    currentStep: 2,
                    progress: 100,
                    status: 'success'
                }));

                // Resume security alerts
                if (securityRef.current) {
                    securityRef.current.resumeSecurityAlerts();
                }

                setTimeout(() => {
                    setSubmissionProgress(prev => ({ ...prev, open: false }));
                    history.push('/thankyou');
                    setSubmissionInProgress(false);
                }, 2000);
            })
            .catch(error => {
                // Resume security alerts on error
                if (securityRef.current) {
                    securityRef.current.resumeSecurityAlerts();
                }

                setSubmissionProgress(prev => ({
                    ...prev,
                    status: 'error',
                    errorMessage: 'Failed to submit your answers. Please try again.'
                }));

                setTimeout(() => {
                    setSubmissionProgress(prev => ({ ...prev, open: false }));
                    setSubmissionInProgress(false);
                }, 3000);
            });
        };
    
    // Save to Firebase
    // await firebase.database().ref(`Exam_results/${examCode}/${userEmail}`).set({
    //   companyEntries,
    //   validationResults: results,
    //   score: finalScore,
    //   timestamp: firebase.database.ServerValue.TIMESTAMP,
    //   // Include other metadata as before
    //   alt: sessionStorage.getItem("countalt"),
    //   ctrl: sessionStorage.getItem("countctrl"),
    //   tab: sessionStorage.getItem("count_tabchange"),
    //   face: sessionStorage.getItem("count_facedetect"),
    //   fullscreen: sessionStorage.getItem("count_fullscreen"),
    //   semail: sessionStorage.getItem("checkemail"),
    //   sname: sessionStorage.getItem("checkname"),
    //   photo: sessionStorage.getItem("imageSrc")
    // });

    // swal({
    //   title: "Validation Complete",
    //   html: `
    //     <div style="text-align: left;">
    //       <h3>Final Score: ${finalScore}/30</h3>
    //       ${results.map((r, i) => `
    //         <div style="margin: 10px 0; padding: 10px; border-left: 3px solid ${r.valid ? '#4CAF50' : '#F44336'}">
    //           <h4>Company ${i + 1}: ${r.score}/10</h4>
    //           <p>${r.details.company.message}</p>
    //           <p>${r.details.email.message}</p>
    //           <p>${r.details.linkedin.message}</p>
    //           <p>Reason: ${r.details.reason.message}</p>
    //         </div>
    //       `).join('')}
    //     </div>
    //   `,
    //   icon: "success"
    // }).then(() => {
    //   history.push('/thankyou');
    // });
    saveResult(); // Start the initial save attempt

  } catch (error) {
    swal("Error", "Failed to validate company details", "error");
  } finally {
    setSubmissionInProgress(false);
  }
};

  function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    for (var i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
    }
    return true; 
  }



  const sanitizeEmail = (email) => email.replace(/\./g, ',');
  // const shuffledQuestions = shuffle(questions); 
  return (
    <div className="dashboard-container">
      {/* Submission Progress Modal */}
      <SubmissionProgressModal
        open={submissionProgress.open}
        onClose={() => setSubmissionProgress(prev => ({ ...prev, open: false }))}
        steps={[
          'Reviewing your brilliant responses...',
          'Securing everything safely...',
          'Almost there...'
        ]}
        currentStep={submissionProgress.currentStep}
        progress={submissionProgress.progress}
        status={submissionProgress.status}
        errorMessage={submissionProgress.errorMessage}
      />
      <div className="left-panel">
        {!isLoadTesting && (
          <div className="detect" style={{ width: '100%', height: '350px', marginBottom: '20px' }}>
            <Suspense fallback={<div>Loading Webcam...</div>}>
              <Detection onCheatDetected={(type, message) => {
                // Record the violation in the security system
                if (securityRef.current) {
                  securityRef.current.recordDetectionViolation(type, message);
                }
              }} />
            </Suspense>
          </div>
        )}
        <div className="lame">
          <p style={{ margin: '5px' }} id="nameTxt">Name: {sessionStorage.getItem("checkname")}</p>
          <p style={{ margin: '5px' }} id="emailTxt">Email: {sessionStorage.getItem("checkemail")}</p>
        </div>
        <div className="instructionsContainer">
          <Typography variant="h6" align="center" id="instructionsTxt" className="instructionsHeader">
            Assessment Guidelines
          </Typography>
          <Typography variant="body2" align="left" className="instructionsText">
            <ul>
              <li><strong>Data Preservation:</strong> Ensure all responses are properly saved before concluding the session.</li>
              <li><strong>Session Integrity:</strong> Maintain browser session - premature exit may result in data loss.</li>
            </ul>
          </Typography>
        </div>
      </div>
      <div className="right-panel">
        <h1 id="headTextExamScreen">Assessment Items</h1>
        <form onSubmit={handleSubmit}>
            {questions.map((question, index) => (
                <div key={index} className="question-container">
                {/* Question number and text */}
                <h3 className="question-text">
                  <span dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(`<pre><code>${index + 1 + ". " + question.text}</code></pre>`) 
                  }} />
                </h3>
              
                {/* If image exists for the question, show it */}
                {question.imageUrl && (
                  <div className="question-image-container">
                    <AuthenticatedImage 
                      imageUrl={question.imageUrl}
                      questionId={question.id}
                      alt={`Question ${index + 1}`} 
                      style={{ 
                        maxHeight: 650, // 30% larger image for better visibility
                        width: '100%'
                      }} 
                    />
                  </div>
                )}
              
                {/* If description exists for psyco questions, show it */}
                {question.problemDescription && (
                  <div style={{ 
                    marginTop: 8, 
                    marginBottom: 12, 
                    padding: 12, 
                    background: '#f5f5f5', 
                    border: '1px solid #ddd',
                    borderRadius: 8 
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#1976d2', fontSize: 14 }}>
                      📋 Options and Scenarios:
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 14 }}>
                      {question.problemDescription}
                    </div>
                  </div>
                )}
              
                {/* Options list below the question */}
                {isPshycoTest === 0 ? (
                  <FormControl component="fieldset" className="custom-form-control">
                    <RadioGroup
                      value={answers[index] || ''}
                      onChange={(e) => handleAnswerChange(index, e.target.value, 0)}
                    >
                      {question.options.map((option, optIndex) => (
                        <FormControlLabel
                          key={optIndex}
                          value={option}
                          id="OptionExamScreen"
                          control={<Radio color="primary" className="custom-radio" />}
                          label={<span className="custom-label">{option}</span>}
                          className="custom-option-label"
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                ) : isPshycoTest === 2 ? (
                  <div className="company-validation-container">
                    <Typography variant="body1" gutterBottom>
                      Format: Company Name | Contact Email | LinkedIn URL | Reason for choosing
                    </Typography>
                    {[0, 1, 2].map((entryIndex) => (
                      <div key={entryIndex} className="company-input-field">
                        <textarea
                          className="company-textarea"
                          value={companyEntries[entryIndex]}
                          onChange={(e) => {
                            const newEntries = [...companyEntries];
                            newEntries[entryIndex] = e.target.value;
                            setCompanyEntries(newEntries);
                          }}
                          placeholder={`Company Name | Contact Email | LinkedIn URL | Reason for choosing\nExample: Acme Corp | john@acme.com | linkedin.com/in/john-doe | They need better customer tracking`}
                        />
                      </div>
                      // <div key={entryIndex} className="company-input-field">
                      //   <textarea
                      //     style={{width: 'calc(100% - 20px)',height: '100px'}}  
                      //     label={`Company ${entryIndex + 1} Details`}
                      //     variant="outlined"
                      //     fullWidth
                      //     multiline
                      //     rows={4}
                      //     value={companyEntries[entryIndex]}
                      //     onChange={(e) => {
                      //       const newEntries = [...companyEntries];
                      //       newEntries[entryIndex] = e.target.value;
                      //       setCompanyEntries(newEntries);
                      //     }}
                      //     placeholder={`Example: Acme Corp | john@acme.com | linkedin.com/in/john-doe | They need better customer tracking`}
                      //     margin="normal"
                      //   />
                      // </div>
                    ))}
                  </div>
                ) : (
                  <div className="custom-input-container">
                    <label htmlFor={`answer-${index}`} className="custom-label">Answer:</label>
                    <input 
                      type="text" 
                      id={`answer-${index}`} 
                      value={answers[index] || ''} 
                      onChange={(e) => handleAnswerChange(index, e.target.value, 1)} 
                      className="custom-input" 
                      maxLength="4"
                      pattern="[ABCD]*"
                      title="Only A, B, C, or D are allowed"
                      placeholder="Type your answer here..." 
                    />
                  </div>
                )}
              </div>
            ))}
            
            {/* Submit button outside the map to ensure it's rendered after all questions */}
            <div className="submit-container">
                <Button variant="contained" id="SubmitButtonExamScreen" color="primary" type="submit" className="submitButton">
                    Submit Answers
                </Button>
            </div>
        </form>

      </div>
    </div>
  );
}

export default TextBasedExamScreen;
